import { RelayEnvironment } from '@/graphql/relay-env';
import { useCallback, useEffect, useState } from 'react';
import { type GraphQLTaggedNode, fetchQuery } from 'react-relay';
import type { Environment, FetchQueryFetchPolicy, OperationType, PayloadError } from 'relay-runtime';

export const useRelayQuery = <T extends OperationType>(
  query: GraphQLTaggedNode,
  variables: T['variables'],
  extraArgs?: {
    refetchOnMount: boolean; // default true
    beforeRefetch?: (variables: T['variables']) => T['variables'];
    beforeSetData?: (data: T['response'], variables: T['variables']) => T['response'];
    environment?: Environment;
    cacheConfig?: {
      fetchPolicy?: FetchQueryFetchPolicy;
    };
  }
) => {
  const [data, setData] = useState<T['response']>(null);
  const [error, setError] = useState<PayloadError | null>(null);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(
    (variables?: T['variables'], onComplete?: (data: T['response'], errors: PayloadError[]) => void) => {
      const input = extraArgs?.beforeRefetch
        ? extraArgs.beforeRefetch(variables as T['variables'])
        : (variables as T['variables']);

      let newData = null as T['response'];
      fetchQuery(extraArgs?.environment ?? RelayEnvironment, query, input, extraArgs?.cacheConfig).subscribe({
        start: () => {
          if (loading) return;
          setLoading(true);
          setError(null);
        },
        next: (data) => {
          const dataFilter = extraArgs?.beforeSetData ? extraArgs.beforeSetData(data, input) : data;
          newData = dataFilter;
          setData(dataFilter);
          setLoading(false);
        },
        error: (error: PayloadError, isUncaughtThrownError?: boolean) => {
          setError(error);
          setLoading(false);
        },
        complete: () => {
          setLoading(false);
          onComplete?.(newData, !!error ? [error] : []);
        },
      });
    },
    [query, variables, setData, setError, setLoading]
  );

  const reset = useCallback(() => {
    setData(null);
    setError(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (extraArgs?.refetchOnMount === false) return;

    refetch(variables);
  }, []);

  return { data, error, loading, refetch, reset };
};
